import { useState, useEffect } from 'react';
import axiosInstance from '../../../utils/axios';
import { SERVER } from '../../../hooks/config';
import { 
  MdAdd, 
  MdSearch, 
  MdEdit, 
  MdDelete, 
  MdVisibility,
  MdImage
} from 'react-icons/md';
import './Categories.scss';
import { MdClose } from "react-icons/md";
const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [viewingCategory, setViewingCategory] = useState(null);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    name: '',
    image: ''
  });

  // File state for uploads
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, [searchTerm]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/categories');
      
      // Filter categories based on search term
      const filteredCategories = response.data.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      setCategories(filteredCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      }

      await axiosInstance.post('/categories', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setShowAddModal(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      console.error('Error adding category:', error);
      alert(error.response?.data?.error || 'Error adding category');
    }
  };

  const handleEditCategory = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      }

      await axiosInstance.put(`/categories/${editingCategory.id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setEditingCategory(null);
      resetForm();
      fetchCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      alert(error.response?.data?.error || 'Error updating category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      try {
        await axiosInstance.delete(`/categories/${categoryId}`);
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        if (error.response?.status === 400) {
          alert(error.response.data.error);
        } else {
          alert('Error deleting category');
        }
      }
    }
  };

  const startEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      image: category.image || ''
    });
  };

  const startView = (category) => {
    setViewingCategory(category);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      image: ''
    });
    setImageFile(null);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <div className="categories-loading">
        <div className="loading-spinner"></div>
        <p>Loading categories...</p>
      </div>
    );
  }

  return (
    <div className="admin-categories">
      {/* Header */}
      <div className="categories-header">
        <div className="header-left">
          <h2>Categories Management</h2>
          <p>Manage your product categories</p>
        </div>
        <button 
          className="add-category-btn"
          onClick={() => setShowAddModal(true)}
        >
          <MdAdd />
          Add Category
        </button>
      </div>

      {/* Search */}
      <div className="categories-search">
        <div className="search-box">
          <MdSearch />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
      </div>

      {/* Categories Grid */}
      <div className="categories-grid">
        {categories.map(category => (
          <div key={category.id} className="category-card">
            <div className="category-image">
              {category.image ? (
                <img src={SERVER+category.image} alt={category.name} />
              ) : (
                <div className="no-image">
                  <MdImage />
                </div>
              )}
            </div>
            
            <div className="category-content">
              <h3>{category.name}</h3>
              <p className="product-count">
                {category.product_count || 0} products
              </p>
            </div>

            <div className="category-actions">
              <button
                className="action-btn view-btn"
                onClick={() => startView(category)}
                title="View Details"
              >
                <MdVisibility />
              </button>
              <button
                className="action-btn edit-btn"
                onClick={() => startEdit(category)}
                title="Edit Category"
              >
                <MdEdit />
              </button>
              <button
                className="action-btn delete-btn"
                onClick={() => handleDeleteCategory(category.id)}
                title="Delete Category"
              >
                <MdDelete />
              </button>
            </div>
          </div>
        ))}

        {categories.length === 0 && (
          <div className="no-categories">
            <p>No categories found</p>
            {searchTerm && (
              <p className="search-hint">
                Try adjusting your search terms
              </p>
            )}
          </div>
        )}
      </div>

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New Category</h3>
              <button onClick={() => setShowAddModal(false)}>
                <MdClose />
              </button>
            </div>
            <form onSubmit={handleAddCategory}>
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter category name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Category Image</label>
                <input
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files[0] || null)}
                />
                {formData.image && (
                  <div className="current-image">
                    <small>Current: {formData.image}</small>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary">
                  Add Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {editingCategory && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Category</h3>
              <button onClick={() => setEditingCategory(null)}>
                <MdClose />
              </button>
            </div>
            <form onSubmit={handleEditCategory}>
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter category name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Category Image</label>
                <input
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files[0] || null)}
                />
                {formData.image && (
                  <div className="current-image">
                    <small>Current: {formData.image}</small>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setEditingCategory(null)}>
                  Cancel
                </button>
                <button type="submit" className="primary">
                  Update Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Category Modal */}
      {viewingCategory && (
        <div className="modal-overlay">
          <div className="modal view-modal">
            <div className="modal-header">
              <h3>Category Details</h3>
              <button onClick={() => setViewingCategory(null)}>
                <MdClose />
              </button>
            </div>
            <div className="category-details">
              <div className="category-header">
                <div className="category-image-large">
                  {viewingCategory.image ? (
                    <img 
                      src={SERVER+viewingCategory.image} 
                      alt={viewingCategory.name}
                    />
                  ) : (
                    <div className="no-image-large">
                      <MdImage />
                    </div>
                  )}
                </div>
                <div className="category-info">
                  <h4>{viewingCategory.name}</h4>
                  <p className="product-count-large">
                    {viewingCategory.product_count || 0} products
                  </p>
                </div>
              </div>

              <div className="category-sections">
                <div className="section">
                  <h5>Category Information</h5>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">ID:</span>
                      <span className="info-value">{viewingCategory.id}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Name:</span>
                      <span className="info-value">{viewingCategory.name}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Products:</span>
                      <span className="info-value">{viewingCategory.product_count || 0}</span>
                    </div>
                  </div>
                </div>

                {viewingCategory.image && (
                  <div className="section">
                    <h5>Image URL</h5>
                    <div className="image-url">
                      <code>{viewingCategory.image}</code>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
